import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HighlightOffOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  styled,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faTimes } from "@fortawesome/pro-light-svg-icons";
import { faCrosshairsSimple } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SettingPopover, SolidCard } from "@utilifeed/components";

import QuickOptionButtonGroup from "../shared/QuickOptionButtonGroup";

type HeatOptimizationOption = {
  label: string;
  value: boolean;
};

export type SettingControllerProps = Readonly<{
  isLoading: boolean;
  isFlexstationError: boolean;
  isFlexstationParametersLoading: boolean;
  heatOptimization: boolean;
  onHeatOptimizationChange?: (value: boolean) => void;
  isAdmin: boolean;
  isFromPlatform: boolean;
  address: string;
  minTemp: number;
  targetTemp: number;
  isInputDirty: boolean;
  popoverAnchorEl: HTMLButtonElement | null;
  stepAmount: number;
  minTempLimit: number;
  maxTempLimit: number;
  setPopoverAnchorEl: (el: HTMLButtonElement | null) => void;
  handleTargetTempChange: (value: number) => void;
  handleNumberKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleNumberPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  handleNumberWheel: (e: React.WheelEvent<HTMLInputElement>) => void;
  handleResetTargetTemp: () => void;
  handleClosePopover: () => void;
  handleConfirmTargetTemp: () => void;
}>;

const StyledNumberField = styled(TextField)(({ theme }) => ({
  width: 200,
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.dark,
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
  },
  "& input": {
    caretColor: "transparent",
    userSelect: "none",
    WebkitAppearance: "textfield",
    MozAppearance: "textfield",
    appearance: "auto",
  },
  "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
    {
      WebkitAppearance: "auto",
      appearance: "auto",
      opacity: 1,
      display: "block",
    },
}));

function IconLabelRow({
  icon,
  label,
  labelColor = "primary",
  iconColor,
}: {
  icon: IconProp;
  label: string;
  labelColor?: "primary" | "error";
  iconColor?: string;
}) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <FontAwesomeIcon icon={icon} size="xl" color={iconColor} style={{ fontSize: "2em" }} />
      <Typography variant="displaySmall" color={labelColor} fontWeight={400} fontSize="2em">
        {label}
      </Typography>
    </Box>
  );
}

export function SettingController({
  isLoading,
  isFlexstationError,
  isFlexstationParametersLoading,
  heatOptimization,
  isAdmin,
  isFromPlatform,
  onHeatOptimizationChange,
  address,
  minTemp,
  targetTemp,
  isInputDirty,
  popoverAnchorEl,
  stepAmount,
  minTempLimit,
  maxTempLimit,
  setPopoverAnchorEl,
  handleTargetTempChange,
  handleNumberKeyDown,
  handleNumberPaste,
  handleNumberWheel,
  handleResetTargetTemp,
  handleClosePopover,
  handleConfirmTargetTemp,
}: SettingControllerProps) {
  const { t } = useTranslation(["propai_properties", "_action"]);
  const theme = useTheme();

  const heatOptimizationOptions: HeatOptimizationOption[] = [
    {
      label: t("text_enabled"),
      value: true,
    },
    {
      label: t("label_disabled"),
      value: false,
    },
  ];

  const [selectedHeatOptimizationOption, setSelectedHeatOptimizationOption] =
    useState<boolean>(heatOptimization);

  useEffect(() => {
    setSelectedHeatOptimizationOption(heatOptimization);
  }, [heatOptimization]);

  const handleChangeHeatOptimizationOption = (option: HeatOptimizationOption) => {
    setSelectedHeatOptimizationOption(option.value);
    onHeatOptimizationChange?.(option.value);
  };

  return (
    <SolidCard title={t("_common:text_settings")}>
      <Grid container columnSpacing={2} rowSpacing={2} alignItems="stretch">
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <SolidCard
            title={t("title_heat_optimization")}
            titleVariant="titleSmall"
            loading={isLoading}
            error={isFlexstationError ? t("message_no_data_available") : undefined}
            sx={{ backgroundColor: "#ffff" }}
            isCircularLoader
          >
            {isFromPlatform ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "50%",
                  mx: "auto",
                }}
              >
                <QuickOptionButtonGroup
                  options={heatOptimizationOptions}
                  activeValue={selectedHeatOptimizationOption}
                  onChange={handleChangeHeatOptimizationOption}
                  ariaLabel={"Enable/Disable Heat Optimization"}
                />
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <IconLabelRow
                  icon={heatOptimization ? faCheck : faTimes}
                  label={heatOptimization ? t("_common:text_enabled") : t("label_disabled")}
                  labelColor={heatOptimization ? "primary" : "error"}
                  iconColor={
                    heatOptimization ? theme.palette.primary.main : theme.palette.error.main
                  }
                />
              </Box>
            )}
          </SolidCard>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
          <SolidCard
            loading={isFlexstationParametersLoading}
            isCircularLoader
            title={t("title_indoor_target_temperature")}
            titleVariant="titleSmall"
            sx={{ backgroundColor: "#ffff" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                gap: 2,
                pb: 2,
              }}
            >
              {isAdmin ? (
                <>
                  <StyledNumberField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={targetTemp}
                    onChange={(e) => handleTargetTempChange(Number(e.target.value))}
                    onKeyDown={handleNumberKeyDown}
                    onPaste={handleNumberPaste}
                    onWheel={handleNumberWheel}
                    InputProps={{
                      inputProps: {
                        step: stepAmount,
                        min: minTempLimit,
                        max: maxTempLimit,
                        tabIndex: -1,
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: "none", alignSelf: "center", borderRadius: "24px" }}
                    onClick={(e) => setPopoverAnchorEl(e.currentTarget)}
                  >
                    {t("action_update")}
                  </Button>
                  <Button
                    disabled={!isInputDirty}
                    variant="outlined"
                    sx={{ textTransform: "none", alignSelf: "center", borderRadius: "24px" }}
                    onClick={handleResetTargetTemp}
                  >
                    {t("_action:action_reset")}
                  </Button>
                </>
              ) : (
                <IconLabelRow
                  icon={faCrosshairsSimple}
                  label={`${minTemp} °C`}
                  iconColor={theme.palette.primary.main}
                />
              )}
            </Box>
          </SolidCard>
        </Grid>
      </Grid>
      <SettingPopover
        id="target-temperature"
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        handleClose={() => setPopoverAnchorEl(null)}
      >
        <Paper variant="outlined" sx={{ p: 2, maxWidth: 400, backgroundColor: "#eef6f4" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6">{t("title_update_indoor_target_temperature")}</Typography>
            <IconButton disableFocusRipple onClick={() => setPopoverAnchorEl(null)}>
              <HighlightOffOutlined />
            </IconButton>
          </Box>
          <Box sx={{ backgroundColor: "#fff", p: 2, borderRadius: "8px" }}>
            <Typography variant="body2" mb={2}>
              {t("text_confirm_target_temperature_message", {
                buildingName: address || " ",
                temp: targetTemp,
              })}
            </Typography>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pt={2}
              columnGap={2}
            >
              <Button
                fullWidth
                variant="contained"
                color="primary"
                sx={{ borderRadius: "24px", textTransform: "none" }}
                onClick={handleConfirmTargetTemp}
              >
                {t("action_confirm")}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                sx={{ borderRadius: "24px", textTransform: "none" }}
                onClick={handleClosePopover}
              >
                {t("action_cancel")}
              </Button>
            </Box>
          </Box>
        </Paper>
      </SettingPopover>
    </SolidCard>
  );
}
